'use client'

import { BlockMini, GROUP_TONE } from '@/components/dashboard/BlockMini'
import type { PersonaSlug } from '@/lib/personas'
import { useT } from '../i18n/LocaleProvider'
import { initialsOf } from './AuthShell'
import { PREVIEW_TILES, type PreviewTile } from './persona-start'

/**
 * The page being made, beside every sign-in screen.
 *
 * Not a picture of the product with a slogan over it: the person's own page,
 * forming as they go. Their name, bio, photo and handle fill in as they type
 * them, and the blocks are the ones their persona leads with, drawn the way
 * the editor's catalog draws them. Picking a persona drops that persona's
 * blocks in one after another; adding a first block lands it on top.
 *
 * Still otherwise. The landing is the one movement, so it reads as the page
 * reacting to a choice rather than as decoration.
 */
export function AuthAside({
  persona = 'person',
  name,
  bio,
  username,
  avatarUrl,
  avatarClassName,
  showCaret = false,
  fresh,
  sample = false,
}: {
  persona?: PersonaSlug | null
  name?: string
  bio?: string
  username?: string
  avatarUrl?: string
  avatarClassName?: string
  showCaret?: boolean
  /** A block about to be added, shown landing on top of the page. */
  fresh?: PreviewTile | null
  /** Someone else's page, for screens with nothing of the reader's to show yet (sign in, reset). */
  sample?: boolean
}) {
  const t = useT()
  const slug = persona ?? 'person'

  const shownName = sample ? t('auth.preview.sampleName') : name || t('auth.profile.fullNamePlaceholder')
  const shownBio = sample ? t('auth.preview.sampleBio') : bio || t('auth.preview.bioPlaceholder')
  const shownHandle = sample ? 'saraa' : username || t('auth.username.placeholder')
  const avatarClass = sample ? 'bg-gradient text-white' : avatarClassName || 'bg-muted text-muted-foreground'
  const initials = initialsOf(shownName) || 'F'

  // Five blocks fit the page without it scrolling. A fresh block goes first
  // and pushes the persona's last one off, which is what adding one does.
  const tiles = fresh ? [fresh, ...PREVIEW_TILES[slug]].slice(0, 5) : PREVIEW_TILES[slug]

  return (
    <div className="w-[19rem] max-w-full" aria-hidden="true">
      <div className="flex flex-col gap-4 rounded-[2rem] border border-border bg-background p-4 shadow-2xl shadow-black/40">
        <div className="flex flex-col items-center gap-2.5 px-2 pt-3 text-center">
          <div className={`flex size-16 items-center justify-center overflow-hidden rounded-full font-gilroy text-lg font-bold ${avatarUrl ? '' : avatarClass}`}>
            { avatarUrl
              // A one-off preview of an upload, not a page asset; see Register.
              // eslint-disable-next-line @next/next/no-img-element
              ? <img src={avatarUrl} alt="" className="size-full object-cover" />
              : initials }
          </div>
          <div className="flex min-w-0 max-w-full flex-col gap-1">
            <span className="truncate font-gilroy text-lg font-bold leading-tight text-foreground">{shownName}</span>
            <span className="line-clamp-2 text-xs font-medium leading-snug text-muted-foreground">{shownBio}</span>
          </div>
          <span className="flex max-w-full items-center rounded-full bg-muted px-3 py-1.5 text-[11px] font-semibold text-muted-foreground">
            fuse.mn/
            <span className="truncate text-foreground">{shownHandle}</span>
            { showCaret && <span className="ml-px h-3 w-px animate-caret-blink bg-foreground" /> }
          </span>
        </div>

        <div className="grid auto-rows-[3.75rem] grid-cols-2 gap-2">
          { tiles.map((tile, index) => {
            const tone = GROUP_TONE[tile.group]
            const isFresh = !!fresh && index === 0
            // Its place in the persona's own list, which a fresh block on top does not change.
            const position = fresh ? index - 1 : index
            return (
              <div
                // Keyed by persona (and by the fresh block's type), so a new
                // choice remounts the tiles and they land again in order.
                key={isFresh ? `fresh-${tile.type}` : `${slug}-${position}`}
                className={`flex animate-land items-center justify-center overflow-hidden rounded-2xl motion-reduce:animate-none ${
                  tile.w === 2 ? 'col-span-2' : ''} ${tile.h === 2 ? 'row-span-2' : ''} ${isFresh ? 'ring-2 ring-white/80 ring-offset-2 ring-offset-background' : ''}`}
                style={{
                  background: `radial-gradient(120% 100% at 0% 0%, rgb(255 255 255 / 0.28), transparent 55%), linear-gradient(135deg, ${tone.from}, ${tone.to})`,
                  animationDelay: isFresh ? '0ms' : `${120 + position * 90}ms`,
                }}
              >
                <BlockMini type={tile.type} group={tile.group} />
              </div>
            )
          }) }
        </div>
      </div>
    </div>
  )
}
